<template>
  <div class="trainer-list-view pa-4">
    <!-- Header Neutro -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-icon color="primary" large class="mr-3">mdi-teach</v-icon>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Capacitadores
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Padrón de ponentes e instructores internos y empresas consultoras externas.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- Filtros de Búsqueda -->
    <v-card class="mb-5 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <s-select
              label="Tipo de Capacitador"
              :items="filterTypes"
              v-model="filters.TrnType"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="4" md="6">
            <s-text v-model="filters.Search" label="Buscar por nombre, documento, organización o especialidad..." clearable />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <v-btn block color="primary" class="rounded-lg font-weight-bold white--text text-none py-4" elevation="0" :loading="loading" @click="fetchTrainers">
              <v-icon left small>mdi-magnify</v-icon>
              Consultar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Grilla de Datos -->
    <v-card class="rounded-xl border-light bg-white pa-4 mb-5" outlined elevation="0">
      <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="grey darken-3" class="mr-2">mdi-account-tie-outline</v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
            Lista de Capacitadores ({{ trainers.length }})
          </span>
        </div>
        <v-btn color="primary" class="rounded-lg text-none font-weight-bold px-4 py-4" small elevation="0" @click="openCreateModal">
          <v-icon left small>mdi-plus</v-icon>
          Nuevo Capacitador
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="trainers"
        :loading="loading"
        dense
        class="border-rounded elevation-0"
        :items-per-page="15"
      >
        <template v-slot:[`item.TrnTypeName`]="{ item }">
          <v-chip small :color="item.TrnType === 1 ? 'primary' : 'grey darken-1'" dark class="font-weight-bold">
            <v-icon left x-small>{{ item.TrnType === 1 ? 'mdi-domain' : 'mdi-earth' }}</v-icon>
            {{ item.TrnTypeName }}
          </v-chip>
        </template>

        <template v-slot:[`item.TrnFullName`]="{ item }">
          <div class="font-weight-bold grey--text text--darken-4">
            {{ item.TrnFullName }}
          </div>
          <div class="text-caption grey--text text--darken-1" v-if="item.TrnDocumentNumber">
            Doc: {{ item.TrnDocumentNumber }}
          </div>
        </template>

        <template v-slot:[`item.TrnCvPath`]="{ item }">
          <v-tooltip bottom v-if="item.TrnCvPath">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="downloadCv(item)">
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>Descargar CV</span>
          </v-tooltip>
          <span v-else class="grey--text text--lighten-1 text-caption">-</span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openEditModal(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="deactivateTrainer(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>Desactivar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Formulario -->
    <TrainerFormModal
      v-model="modal.show"
      :edit-item="modal.item"
      @saved="fetchTrainers"
    />
  </div>
</template>

<script>
import SText from "@/components/Utils/SText.vue";
import SSelect from "@/components/Utils/SSelect.vue";
import TrainerService from "@/services/Management/TrainerService";
import TrainerFormModal from "./TrainerFormModal.vue";
import _GenUploadFiles from "@/services/General/GenUploadFiles";

export default {
  name: "TrainerList",
  components: {
    SText,
    SSelect,
    TrainerFormModal,
  },
  data() {
    return {
      loading: false,
      filterTypes: [
        { text: "Todos los tipos", value: null },
        { text: "Interno", value: 1 },
        { text: "Externo", value: 2 },
      ],
      filters: {
        TrnType: null,
        Search: "",
        SecStatus: 1,
      },
      trainers: [],
      headers: [
        { text: "ID", value: "TrnID", align: "center", width: "70px" },
        { text: "Tipo", value: "TrnTypeName", width: "120px" },
        { text: "Nombre Completo", value: "TrnFullName", align: "start" },
        { text: "Organización", value: "TrnOrganization", width: "200px" },
        { text: "Especialidad", value: "TrnSpecialty", width: "180px" },
        { text: "Correo", value: "TrnEmail", width: "180px" },
        { text: "Teléfono", value: "TrnPhone", width: "130px" },
        { text: "CV", value: "TrnCvPath", align: "center", width: "90px" },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: "110px" },
      ],
      modal: {
        show: false,
        item: null,
      },
    };
  },
  created() {
    this.fetchTrainers();
  },
  methods: {
    fetchTrainers() {
      this.loading = true;
      const reqId = this.$fun.getUserID();
      TrainerService.list(this.filters, reqId)
        .then((res) => {
          this.trainers = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.$fun.alert("Error al cargar la lista de capacitadores.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadCv(item) {
      if (!item || !item.TrnCvPath) return;
      _GenUploadFiles.getFile("PathTrainingTrainerCV", item.TrnCvPath)
        .catch(() => {
          this.$fun.alert("No se pudo descargar el archivo de CV.", "error");
        });
    },
    openCreateModal() {
      this.modal.item = null;
      this.modal.show = true;
    },
    openEditModal(item) {
      this.modal.item = Object.assign({}, item);
      this.modal.show = true;
    },
    deactivateTrainer(item) {
      this.$fun.alert("¿Está seguro de desactivar este capacitador?", "question").then((r) => {
        if (r && r.value) {
          const reqId = this.$fun.getUserID();
          const payload = Object.assign({}, item, { SecStatus: 0 });
          TrainerService.save(payload, reqId)
            .then(() => {
              this.$fun.alert("Capacitador desactivado correctamente.", "success");
              this.fetchTrainers();
            })
            .catch(() => {
              this.$fun.alert("Error al desactivar capacitador.", "error");
            });
        }
      });
    },
  },
};
</script>
